import React, { createContext, useContext, useCallback, useRef, useSyncExternalStore, PropsWithChildren } from "react";
import type { EntityTablePageConfig } from "@/types/providers/entityTablePage";

type StoreContextValue = {
  register: (config: EntityTablePageConfig) => void;
  unregister: () => void;
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => EntityTablePageConfig | null;
};

const StoreContext = createContext<StoreContextValue | null>(null);

export function EntityTablePageProvider({ children }: PropsWithChildren) {
  const configRef = useRef<EntityTablePageConfig | null>(null);
  const listenersRef = useRef(new Set<() => void>());

  const notify = useCallback(() => {
    listenersRef.current.forEach((l) => l());
  }, []);

  const register = useCallback(
    (cfg: EntityTablePageConfig) => {
      configRef.current = cfg;
      notify();
    },
    [notify],
  );

  const unregister = useCallback(() => {
    configRef.current = null;
    notify();
  }, [notify]);

  const subscribe = useCallback((listener: () => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const getSnapshot = useCallback(() => configRef.current, []);

  const value = React.useMemo(
    () => ({ register, unregister, subscribe, getSnapshot }),
    [register, unregister, subscribe, getSnapshot],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

/** Used by EntityTablePage to push config — never causes re-renders */
export function useEntityTablePageWriter() {
  const store = useContext(StoreContext);
  if (!store) throw new Error("useEntityTablePageWriter must be used within EntityTablePageProvider");
  return { register: store.register, unregister: store.unregister };
}

const noopSubscribe = () => () => {};
const noopGetSnapshot = () => null;

/** Used by header components — only re-renders when config changes */
export function useEntityTablePageConfig() {
  const store = useContext(StoreContext);
  return useSyncExternalStore(store?.subscribe ?? noopSubscribe, store?.getSnapshot ?? noopGetSnapshot);
}
